/**
 * Player names: what everyone sees in place of a peer id. Each player starts
 * with a generated "Adjective Noun" (`randomName`), kept in this browser, and
 * may rename themselves; the name rides in their peer metadata, so a rename
 * reaches everyone. Ids stay the keys everywhere: names are display only,
 * aren't unique and never reach the sim.
 */

/** Longest name, after `cleanName`. */
export const MAX_NAME_LENGTH = 24;

const ADJECTIVES = [
  'Jolly',
  'Erroneous',
  'Chirpy',
  'Brave',
  'Sleepy',
  'Grumpy',
  'Dapper',
  'Sneaky',
  'Mighty',
  'Wobbly',
  'Fearless',
  'Cheeky',
  'Gallant',
  'Humble',
  'Jaunty',
  'Peckish',
  'Plucky',
  'Rowdy',
  'Sly',
  'Stubborn',
  'Thrifty',
  'Wily',
  'Zealous',
  'Bashful',
  'Bold',
  'Crafty',
  'Dizzy',
  'Eager',
  'Fancy',
  'Gentle',
  'Hasty',
  'Lucky',
  'Merry',
  'Nimble',
  'Pompous',
  'Quirky',
  'Rusty',
  'Scrappy',
  'Tidy',
  'Wise',
  'Breezy',
  'Clumsy',
  'Dashing',
  'Frosty',
  'Gloomy',
  'Hearty',
  'Lofty',
  'Noble',
];

const NOUNS = [
  'Swagman',
  'Donkey',
  'Accountant',
  'Badger',
  'Baker',
  'Blacksmith',
  'Captain',
  'Cobbler',
  'Duke',
  'Farmer',
  'Ferret',
  'Gardener',
  'Goose',
  'Hermit',
  'Juggler',
  'Knight',
  'Lighthouse',
  'Llama',
  'Magpie',
  'Miller',
  'Monk',
  'Otter',
  'Pirate',
  'Plumber',
  'Potato',
  'Quartermaster',
  'Raccoon',
  'Sailor',
  'Scholar',
  'Shepherd',
  'Squire',
  'Tailor',
  'Toad',
  'Turnip',
  'Walrus',
  'Weasel',
  'Wizard',
  'Yak',
  'Admiral',
  'Bard',
  'Beekeeper',
  'Chancellor',
  'Crow',
  'Ferryman',
  'Hedgehog',
  'Innkeeper',
  'Mapmaker',
  'Pelican',
];

/** A fresh "Adjective Noun". `random` is for tests. */
export function randomName(random: () => number = Math.random): string {
  const pick = (words: readonly string[]) => words[Math.floor(random() * words.length)];
  return `${pick(ADJECTIVES)} ${pick(NOUNS)}`;
}

/**
 * A name as typed (ours) or received (a peer's, untrusted): control
 * characters dropped, whitespace collapsed, trimmed and capped. Empty means
 * no name.
 */
export function cleanName(raw: string): string {
  return (
    raw
      // biome-ignore lint/suspicious/noControlCharactersInRegex: stripping them is the point.
      .replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2066-\u2069]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, MAX_NAME_LENGTH)
      .trim()
  );
}

/** What to call `id`: its (cleaned) name, else a short form of the id. */
export function displayName(id: string, name: string | null | undefined): string {
  const clean = name ? cleanName(name) : '';
  return clean || id.slice(0, 8);
}

const NAME_KEY = 'territile.name';

/** This browser's name: the one saved, else a new one (saved for next time). */
export function loadOwnName(): string {
  try {
    const saved = cleanName(localStorage.getItem(NAME_KEY) ?? '');
    if (saved) return saved;
  } catch {
    // Blocked storage: a new name each visit.
  }
  const name = randomName();
  saveOwnName(name);
  return name;
}

export function saveOwnName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Blocked storage: the name lasts this visit.
  }
}
